import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createReactUser, fetchReactUsers } from "../utils/api";
import { logout } from "../utils/auth";
import DashboardSidebar from "../components/DashboardSidebar";

export default function AdminUsersPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    username: "",
    password: "",
    email: "",
    displayName: "",
    roleName: "Employee",
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState("");

  const onChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const loadUsers = useCallback(async () => {
    setUsersLoading(true);
    setUsersError("");
    try {
      const data = await fetchReactUsers();
      setUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      setUsersError(err.message || "Failed to load users.");
    }
    setUsersLoading(false);
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");
    setError("");

    try {
      const res = await createReactUser(form);
      setMessage(res?.message || "User created successfully.");
      setForm({
        username: "",
        password: "",
        email: "",
        displayName: "",
        roleName: "Employee",
      });
      loadUsers();
    } catch (err) {
      setError(err.message || "Failed to create user.");
    }

    setLoading(false);
  };

  return (
    <div className="page-dashboard" style={{ minHeight: "100vh" }}>
      <DashboardSidebar onLogout={handleLogout} />

      <div className="dash-body">
        <nav className="dash-topbar">
          <div className="topbar-left">
            <h2 className="topbar-title">Admin User Management</h2>
            <span className="topbar-greeting">Create access for staff, reviewers, and administrators</span>
          </div>
          <div className="topbar-right">
            <span className="admin-users-badge">Admin only</span>
          </div>
        </nav>

        <div className="dash-content fade-in">
          <div className="admin-users-layout">
          

            <section className="admin-users-panel admin-users-form-panel">
              <div className="admin-users-section-head">
                <div>
                  <h3>Create New User</h3>
                  <p>Add credentials and assign the correct application role.</p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="survey-fields admin-users-form">
                {/* Row 1: Username · Display Name · Email · Role */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "12px" }}>
                  <div className="survey-field">
                    <label>Username</label>
                    <input value={form.username} onChange={(e) => onChange("username", e.target.value)} placeholder="Username" required />
                  </div>
                  <div className="survey-field">
                    <label>Display Name</label>
                    <input value={form.displayName} onChange={(e) => onChange("displayName", e.target.value)} placeholder="Display name" />
                  </div>
                  <div className="survey-field">
                    <label>Email</label>
                    <input type="email" value={form.email} onChange={(e) => onChange("email", e.target.value)} placeholder="name@company.com" />
                  </div>
                  <div className="survey-field">
                    <label>Role</label>
                    <select value={form.roleName} onChange={(e) => onChange("roleName", e.target.value)}>
                      <option value="Employee">Employee</option>
                      <option value="Admin">Admin</option>
                    </select>
                  </div>
                </div>

                {/* Row 2: Password · Create User button */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "12px", alignItems: "flex-end" }}>
                  <div className="survey-field" style={{ marginBottom: 0 }}>
                    <label>Password</label>
                    <input type="password" value={form.password} onChange={(e) => onChange("password", e.target.value)} placeholder="Create a secure password" required />
                  </div>
                  <button className="btn-primary-action admin-users-submit" type="submit" disabled={loading} style={{ whiteSpace: "nowrap" }}>
                    {loading ? "Creating…" : "Create User"}
                  </button>
                </div>

                {message && <div className="form-alert alert-success">{message}</div>}
                {error && <div className="form-alert alert-error">{error}</div>}
              </form>
            </section>

            {/* ── Registered Users Table ── */}
            <section className="admin-users-panel">
              <div className="admin-users-section-head">
                <div>
                  <h3>Registered Users</h3>
                  <p>All users with access to this application.</p>
                </div>
                <button className="btn-outline-action btn-sm" onClick={loadUsers} disabled={usersLoading}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                  </svg>
                  Refresh
                </button>
              </div>

              {usersError && <div className="form-alert alert-error">{usersError}</div>}

              <div className="table-responsive">
                <table className="premium-table">
                  <thead>
                    <tr>
                      <th>Display Name</th>
                      <th>Username</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usersLoading ? (
                      <tr><td colSpan={5} className="text-center text-muted" style={{ padding: "28px 0" }}>Loading users…</td></tr>
                    ) : users.length === 0 ? (
                      <tr><td colSpan={5} className="text-center text-muted" style={{ padding: "28px 0" }}>No users found.</td></tr>
                    ) : (
                      users.map((u) => (
                        <tr key={u.id}>
                          <td><strong>{u.displayName}</strong></td>
                          <td>{u.username}</td>
                          <td>{u.email || "—"}</td>
                          <td>
                            <span className={`status-badge ${u.isAdmin ? "badge-admin" : "badge-employee"}`}>
                              {u.isAdmin ? "Admin" : (u.roles?.[0] || "Employee")}
                            </span>
                          </td>
                          <td>
                            <span className={`status-badge ${u.isActive ? "badge-active" : "badge-inactive"}`}>
                              {u.isActive ? "Active" : "Inactive"}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
